import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Filter, X, Sparkles } from "lucide-react";

interface Category {
  id: number;
  name: string;
}

interface Brand {
  id: number;
  name: string;
}

interface StickyFilterBarProps {
  nameFilter: string;
  categoryFilter: string | string[];
  brandFilter: string;
  priceRange: [number, number];
  maxPrice: number;
  categories: Category[];
  brands: Brand[];
  onOpenFilters: () => void;
  resetFilters: () => void;
  setNameFilter: (value: string) => void;
  setCategoryFilter: (value: string | string[]) => void;
  setBrandFilter: (value: string) => void;
  setPriceRange: (value: [number, number]) => void;
}

const StickyFilterBar = ({
  nameFilter,
  categoryFilter,
  brandFilter,
  priceRange,
  maxPrice,
  categories,
  brands,
  onOpenFilters,
  resetFilters,
  setNameFilter,
  setCategoryFilter,
  setBrandFilter,
  setPriceRange,
}: StickyFilterBarProps) => {
  // Handle multiple categories
  const selectedCategories = Array.isArray(categoryFilter) 
    ? categoryFilter 
    : categoryFilter === "all" ? [] : [categoryFilter];

  const hasActiveFilters =
    nameFilter ||
    selectedCategories.length > 0 ||
    brandFilter !== "all" ||
    priceRange[0] > 0 ||
    priceRange[1] < maxPrice;

  const activeFiltersCount = [
    nameFilter,
    selectedCategories.length > 0,
    brandFilter !== "all",
    priceRange[0] > 0 || priceRange[1] < maxPrice,
  ].filter(Boolean).length;

  // Handle category toggle for multi-select
  const handleCategoryToggle = (categoryId: string) => {
    if (categoryId === "all") {
      setCategoryFilter("all");
      return;
    }

    const newSelected = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    setCategoryFilter(newSelected.length > 0 ? newSelected : "all");
  };

  return (
    <div className="sticky top-0 z-40 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md border-b border-neutral-200 dark:border-neutral-700 md:hidden">
      <div className="px-4 py-3">
        {/* Main Filter Button */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onOpenFilters}
            className={`flex-1 h-12 px-4 rounded-2xl border-2 transition-all duration-200 ${
              hasActiveFilters 
                ? "border-brand-primary bg-brand-gradient/10 hover:bg-brand-gradient/20" 
                : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"
            }`}
          >
            <div className="flex items-center gap-3 w-full">
              <div className="flex items-center gap-2">
                {hasActiveFilters ? (
                  <Sparkles className="h-5 w-5 text-brand-primary" />
                ) : (
                  <Filter className="h-5 w-5 text-neutral-400" />
                )}
                <span className="font-medium text-neutral-700 dark:text-neutral-300">
                  Filtros
                </span>
                {activeFiltersCount > 0 && (
                  <Badge className="bg-brand-gradient text-black font-semibold min-w-[1.5rem] h-6">
                    {activeFiltersCount}
                  </Badge>
                )}
              </div>
            </div>
          </Button>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="icon"
              onClick={resetFilters}
              className="h-12 w-12 rounded-2xl hover:bg-red-50 dark:hover:bg-red-950 text-red-500 hover:text-red-600 border-2 border-transparent hover:border-red-200 dark:hover:border-red-800"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Active Filters Pills - Only show if filters are active */}
        {hasActiveFilters && (
          <div className="mt-3 flex flex-wrap gap-2 overflow-x-auto pb-1">
            {nameFilter && (
              <Badge className="bg-blue-500/90 text-white border-0 pr-1 rounded-full whitespace-nowrap">
                "{nameFilter.length > 12 ? nameFilter.slice(0, 12) + '...' : nameFilter}"
                <button
                  onClick={() => setNameFilter("")}
                  className="ml-2 hover:bg-blue-600 rounded-full p-0.5 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            
            {/* Multiple Categories */}
            {selectedCategories.length > 0 && selectedCategories.map(categoryId => {
              const category = categories.find(c => c.id.toString() === categoryId.toString());
              return category ? (
                <Badge key={categoryId} className="bg-green-500/90 text-white border-0 pr-1 rounded-full whitespace-nowrap">
                  {category.name}
                  <button
                    onClick={() => handleCategoryToggle(categoryId)}
                    className="ml-2 hover:bg-green-600 rounded-full p-0.5 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ) : null;
            })}
            
            {brandFilter !== "all" && (
              <Badge className="bg-purple-500/90 text-white border-0 pr-1 rounded-full whitespace-nowrap">
                {brands.find(b => b.id.toString() === brandFilter)?.name}
                <button
                  onClick={() => setBrandFilter("all")}
                  className="ml-2 hover:bg-purple-600 rounded-full p-0.5 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            
            {(priceRange[0] > 0 || priceRange[1] < maxPrice) && (
              <Badge className="bg-orange-500/90 text-white border-0 pr-1 rounded-full whitespace-nowrap">
                S/{priceRange[0]}-S/{priceRange[1]}
                <button
                  onClick={() => setPriceRange([0, maxPrice])}
                  className="ml-2 hover:bg-orange-600 rounded-full p-0.5 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StickyFilterBar;